import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';
import html2canvas from 'html2canvas';

export type AttachmentKind = 'image' | 'pdf';

export interface FeedbackAttachment {
  id: string;
  fileName: string;
  contentType: string;
  dataUrl: string; // full data URL (data:<mime>;base64,<...>)
  kind: AttachmentKind;
  size: number; // raw bytes (decoded)
}

// Base64 rides inline in the submit-feedback JSON body, which passes through
// API Gateway's ~10MB request limit. Keep per-file and total caps comfortably
// under that once base64 (~+33%) and JSON overhead are accounted for.
export const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB per file
export const MAX_TOTAL_BYTES = 6 * 1024 * 1024; // 6 MB across all attachments

const rawBytesFromDataUrl = (dataUrl: string): number => {
  const idx = dataUrl.indexOf(',');
  const b64 = idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
  if (!b64) return 0;
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
};

interface AddResult {
  ok: boolean;
  error?: string;
}

interface FeedbackContextType {
  isModalOpen: boolean;
  isBannerVisible: boolean;
  isCapturing: boolean;
  attachments: FeedbackAttachment[];
  attachmentError: string;
  openModal: () => void;
  closeModal: () => void;
  showBanner: () => void;
  hideBanner: () => void;
  captureScreenshot: () => Promise<void>;
  addAttachment: (att: Omit<FeedbackAttachment, 'id' | 'size'> & { id?: string }) => AddResult;
  removeAttachment: (id: string) => void;
  updateAttachment: (id: string, dataUrl: string) => void;
  clearAttachments: () => void;
  clearAttachmentError: () => void;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return context;
};

interface FeedbackProviderProps {
  children: ReactNode;
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `att-${Math.floor(performance.now() * 1000)}`;

export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [attachments, setAttachments] = useState<FeedbackAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState('');

  // Mirror of attachments so synchronous size checks read the latest value
  // even when multiple files are added in the same tick (e.g. multi-file upload).
  const attachmentsRef = useRef<FeedbackAttachment[]>([]);
  const screenshotSeq = useRef(0);

  const commit = (next: FeedbackAttachment[]) => {
    attachmentsRef.current = next;
    setAttachments(next);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsBannerVisible(false);
    setIsCapturing(false);
    commit([]);
    setAttachmentError('');
  };

  const showBanner = () => {
    setIsModalOpen(false);
    setIsBannerVisible(true);
  };

  const hideBanner = () => {
    setIsBannerVisible(false);
    setIsModalOpen(true);
  };

  const addAttachment: FeedbackContextType['addAttachment'] = (att) => {
    const size = rawBytesFromDataUrl(att.dataUrl);

    if (size > MAX_FILE_BYTES) {
      const error = `"${att.fileName}" is larger than ${Math.round(MAX_FILE_BYTES / 1024 / 1024)} MB and was not added.`;
      setAttachmentError(error);
      return { ok: false, error };
    }

    const currentTotal = attachmentsRef.current.reduce((sum, a) => sum + a.size, 0);
    if (currentTotal + size > MAX_TOTAL_BYTES) {
      const error = `Attachments exceed the ${Math.round(MAX_TOTAL_BYTES / 1024 / 1024)} MB total limit. "${att.fileName}" was not added.`;
      setAttachmentError(error);
      return { ok: false, error };
    }

    const entry: FeedbackAttachment = {
      id: att.id ?? newId(),
      fileName: att.fileName,
      contentType: att.contentType,
      dataUrl: att.dataUrl,
      kind: att.kind,
      size,
    };
    setAttachmentError('');
    commit([...attachmentsRef.current, entry]);
    return { ok: true };
  };

  const removeAttachment = (id: string) => {
    setAttachmentError('');
    commit(attachmentsRef.current.filter((a) => a.id !== id));
  };

  const updateAttachment = (id: string, dataUrl: string) => {
    commit(
      attachmentsRef.current.map((a) =>
        a.id === id ? { ...a, dataUrl, size: rawBytesFromDataUrl(dataUrl) } : a
      )
    );
  };

  const clearAttachments = () => {
    commit([]);
    setAttachmentError('');
  };

  const clearAttachmentError = () => setAttachmentError('');

  const captureScreenshot = async () => {
    setIsBannerVisible(false);
    setIsCapturing(true);

    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 0.5,
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      screenshotSeq.current += 1;
      addAttachment({
        fileName: `screenshot-${screenshotSeq.current}.jpg`,
        contentType: 'image/jpeg',
        dataUrl,
        kind: 'image',
      });
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
    }

    setIsCapturing(false);
    setIsModalOpen(true);
  };

  const value: FeedbackContextType = {
    isModalOpen,
    isBannerVisible,
    isCapturing,
    attachments,
    attachmentError,
    openModal,
    closeModal,
    showBanner,
    hideBanner,
    captureScreenshot,
    addAttachment,
    removeAttachment,
    updateAttachment,
    clearAttachments,
    clearAttachmentError,
  };

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};
